import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { PenggunaLayout, InspectorLayout, AdminLayout } from "./layouts/RoleLayouts";
import LandingPage from "./pages/LandingPage";
import InspectorsPublicPage from "./pages/InspectorsPublicPage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import SelectRolePage from "./pages/SelectRolePage";
import InspectorDashboard from "./pages/InspectorDashboard";
import UserDashboard from "./pages/UserDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import OrdersPage from "./pages/shared/OrdersPage";
import OrderDetailPage from "./pages/shared/OrderDetailPage";
import ChatPage from "./pages/shared/ChatPage";
import NotificationsPage from "./pages/shared/NotificationsPage";
import ReviewsPage from "./pages/shared/ReviewsPage";
import ReportsPage from "./pages/shared/ReportsPage";
import SearchInspectorsPage from "./pages/pengguna/SearchInspectorsPage";
import InspectorDetailPage from "./pages/pengguna/InspectorDetailPage";
import FavoritesPage from "./pages/pengguna/FavoritesPage";
import PaymentsPage from "./pages/pengguna/PaymentsPage";
import AccountPage from "./pages/pengguna/AccountPage";
import SchedulePage from "./pages/inspector/SchedulePage";
import EarningsPage from "./pages/inspector/EarningsPage";
import PayoutsPage from "./pages/inspector/PayoutsPage";
import InspectorProfilePage from "./pages/inspector/InspectorProfilePage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminInspectorsPage from "./pages/admin/AdminInspectorsPage";
import AdminFinancePage from "./pages/admin/AdminFinancePage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 10000 } } });

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/inspector" element={<InspectorsPublicPage />} />
      <Route path="/masuk" element={<LoginPage />} />
      <Route path="/pilih-peran" element={<SelectRolePage />} />

      <Route element={<ProtectedRoute roles={["pengguna"]} />}>
        <Route element={<PenggunaLayout />}>
          <Route path="/dashboard/pengguna" element={<UserDashboard />} />
          <Route path="/pengguna/pesanan" element={<OrdersPage />} />
          <Route path="/pengguna/pesanan/:orderId" element={<OrderDetailPage />} />
          <Route path="/pengguna/cari" element={<SearchInspectorsPage />} />
          <Route path="/pengguna/inspector/:inspectorId" element={<InspectorDetailPage />} />
          <Route path="/pengguna/favorit" element={<FavoritesPage />} />
          <Route path="/pengguna/chat" element={<ChatPage />} />
          <Route path="/pengguna/chat/:orderId" element={<ChatPage />} />
          <Route path="/pengguna/laporan" element={<ReportsPage />} />
          <Route path="/pengguna/pembayaran" element={<PaymentsPage />} />
          <Route path="/pengguna/ulasan" element={<ReviewsPage />} />
          <Route path="/pengguna/notifikasi" element={<NotificationsPage />} />
          <Route path="/pengguna/akun" element={<AccountPage />} />
        </Route>
      </Route>

      <Route element={<ProtectedRoute roles={["inspector"]} />}>
        <Route element={<InspectorLayout />}>
          <Route path="/dashboard/inspector" element={<InspectorDashboard />} />
          <Route path="/inspector/pesanan" element={<OrdersPage />} />
          <Route path="/inspector/pesanan/:orderId" element={<OrderDetailPage />} />
          <Route path="/inspector/jadwal" element={<SchedulePage />} />
          <Route path="/inspector/laporan" element={<ReportsPage />} />
          <Route path="/inspector/pendapatan" element={<EarningsPage />} />
          <Route path="/inspector/pencairan" element={<PayoutsPage />} />
          <Route path="/inspector/profil" element={<InspectorProfilePage />} />
          <Route path="/inspector/chat" element={<ChatPage />} />
          <Route path="/inspector/chat/:orderId" element={<ChatPage />} />
          <Route path="/inspector/ulasan" element={<ReviewsPage />} />
          <Route path="/inspector/notifikasi" element={<NotificationsPage />} />
        </Route>
      </Route>

      <Route element={<ProtectedRoute roles={["admin"]} />}>
        <Route element={<AdminLayout />}>
          <Route path="/dashboard/admin" element={<AdminDashboard />} />
          <Route path="/admin/pengguna" element={<AdminUsersPage />} />
          <Route path="/admin/inspector" element={<AdminInspectorsPage />} />
          <Route path="/admin/verifikasi" element={<AdminInspectorsPage pendingOnly />} />
          <Route path="/admin/pesanan" element={<OrdersPage />} />
          <Route path="/admin/pesanan/:orderId" element={<OrderDetailPage />} />
          <Route path="/admin/keuangan" element={<AdminFinancePage />} />
          <Route path="/admin/pengaturan" element={<AdminSettingsPage />} />
          <Route path="/admin/notifikasi" element={<NotificationsPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LanguageProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
          </LanguageProvider>
        </AuthProvider>
      </QueryClientProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
